import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Rocket, Users, CreditCard, ShieldCheck, FileBadge, Lightbulb,
  Wrench, Mail, BookOpen, Code2, AlertTriangle, ExternalLink,
} from "lucide-react";

const SECTIONS = [
  {
    icon: Rocket,
    title: "Daily Operations",
    color: "text-cyan-600",
    items: [
      { label: "Monitor Live Subscribers", body: "Subscribers tab → see active/trialing/past-due accounts. Click any row to drill into their plan + users." },
      { label: "Track Trial-to-Paid Conversion", body: "Analytics tab → look at \"Status breakdown\" — trialing → active means a free trial converted to paid. Goal: ≥ 25% conversion in your first 6 months." },
      { label: "Inbox & Support", body: "Customers email you via the in-app Contact Support flow → land in your Resend inbox at businessgenie.bg@gmail.com. Aim to respond within 4 business hours." },
    ],
  },
  {
    icon: CreditCard,
    title: "Billing & Subscriptions",
    color: "text-emerald-600",
    items: [
      { label: "Trial → Paid Flow", body: "Customers sign up free for 30 days (no credit card). When their trial ends, they're prompted to add a credit card to continue. Their data is retained for 90 days even if they don't subscribe." },
      { label: "Failed Payment Recovery", body: "Stripe auto-retries 4 times over 3 weeks (\"smart retries\"). Past-due tenants get a 7-day grace period before access restricts." },
      { label: "Refund Policy", body: "Pro-rate refunds for the unused portion of the current billing period. Issue via Stripe Dashboard → Payment → Refund." },
    ],
  },
  {
    icon: ShieldCheck,
    title: "Compliance & Legal",
    color: "text-rose-600",
    items: [
      { label: "Tax Collection", body: "Stripe Tax is enabled — automatic-tax calculation runs on every checkout. Verify your registrations in Stripe → Tax → Registrations match the Licenses tab here." },
      { label: "GDPR / CCPA Requests", body: "When a customer requests data deletion → use Owner Console → Subscribers → 3-dot menu → \"Delete tenant + all data\". Allow 30 days to comply. Log it under Notes for audit trail." },
      { label: "Legal Doc Updates", body: "Edit Terms / Privacy at /admin/legal — the customer-facing pages at /legal/terms and /legal/privacy update in real time. Version is auto-bumped on save." },
    ],
  },
  {
    icon: FileBadge,
    title: "Recommended Licenses to Maintain",
    color: "text-amber-600",
    items: [
      { label: "Federal/State Business License", body: "Required to legally operate in your jurisdiction. Renew annually. Add it under Licenses tab so you don't miss the renewal." },
      { label: "Sales Tax Permits", body: "Required in every US state or CA province where you cross the economic nexus threshold ($100K or 200 transactions in most US states). Stripe Tax helps detect this." },
      { label: "GDPR Data Processor Registration", body: "Mandatory if you serve EU customers AND process personal data. Register with your data-protection authority or appoint an EU representative." },
      { label: "SOC 2 / ISO 27001 (future)", body: "Once you cross 50+ paying tenants — enterprise buyers will request these. Plan for SOC 2 Type II audit (~$15K, 12-month observation window)." },
    ],
  },
  {
    icon: Lightbulb,
    title: "Intellectual Property Strategy",
    color: "text-violet-600",
    items: [
      { label: "Trademark First (cheapest)", body: '"Business Genie" word mark + logo. ~$350 USPTO (per class), 6-12 months to register. File in your home country first, then Madrid Protocol for EU/UK/AU.' },
      { label: "Copyright (automatic + cheap)", body: "Source code is auto-copyrighted on creation, BUT registering with US Copyright Office ($65) gives you statutory damages in court. Worth it for the core repo." },
      { label: "Patents (expensive, slow)", body: "Software patents are hard and pricey ($10K+ over 3 years). Only worth it if you have a genuine algorithmic novelty. Skip unless seeking VC funding." },
      { label: "Trade Secrets", body: "Best for things you'll never disclose — internal pricing algorithms, AI prompt templates, customer-acquisition playbooks. Protect with NDAs + access controls. Log them here for due-diligence later." },
      { label: "Domain Names", body: "Lock down yourbusinessgenie.com + reasonable typos + .ai/.co/.app variants. Set auto-renewal AND owner-verified emails (loss-of-access = brand emergency)." },
    ],
  },
  {
    icon: Wrench,
    title: "Operational How-To",
    color: "text-slate-600",
    items: [
      { label: "Create a Test Tenant", body: "Run cd /app/backend && python seed_launch_prep_accounts.py from your local. Owner = launch_owner@test.com / Launch123!" },
      { label: "Purge Test Accounts", body: "Test Accounts tab → \"Purge all\" button, OR run python scripts/purge_test_accounts.py --execute. Production must stay 100% test-free before each marketing push." },
      { label: "Deploy Changes to Production", body: "Push to GitHub (\"Save to GitHub\" button) → Render auto-builds and deploys in ~3-5 min. Always check Render logs for the EMERGENT_LLM_KEY env var on first deploy." },
      { label: "Backup Strategy", body: "Render PostgreSQL has daily auto-backups (7-day retention on free, 30 days on paid). Consider enabling Point-in-Time Recovery once you have paying customers." },
    ],
  },
  {
    icon: Mail,
    title: "Customer Communication",
    color: "text-blue-600",
    items: [
      { label: "Trial Reminder Emails", body: "Auto-sent 7 days, 1 day, and at trial-end via Resend (configured in jobs/trial_reminders.py). Customise copy in services/email_service.py." },
      { label: "Bulk Welcome on Import", body: "When a customer bulk-uploads clients via your Bulk AI Upload feature, they can optionally toggle \"Send welcome email\" — if on, each imported client gets your business's branded welcome via Resend." },
      { label: "Outage Notifications", body: "Discord webhook fires automatically on trial-expired events. Add a status page (statuspage.io free tier or BetterUptime) before you cross 100 paying tenants." },
    ],
  },
  {
    icon: Code2,
    title: "Stack & Integration Reference",
    color: "text-indigo-600",
    items: [
      { label: "Frontend Hosting", body: "Render Static Site → CDN-distributed. Build = yarn build → /app/frontend/build." },
      { label: "Backend Hosting", body: "Render Web Service running FastAPI/Uvicorn. Auto-restart on push. Health check on GET /api/health." },
      { label: "Database", body: "Render PostgreSQL Managed. Connection string in MONGO_URL (legacy name; actually Postgres URL). Backups daily." },
      { label: "External APIs", body: "Stripe (payments + tax), Resend (transactional email), Emergent LLM (AI + object storage). Keys in Render env vars. All listed under Internal Accounts tab." },
    ],
  },
];

export default function HowToGuide() {
  return (
    <div className="space-y-4" data-testid="howto-guide">
      <Card className="border-cyan-200/40 bg-gradient-to-br from-cyan-50/40 to-transparent">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-cyan-600" />
            Operator's Handbook
            <Badge variant="outline" className="ml-2 text-xs">Owner-only</Badge>
          </CardTitle>
          <CardDescription>
            How to run Business Genie as a business — billing, compliance, IP, daily ops. Bookmark this tab.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SECTIONS.map((section, idx) => {
          const Icon = section.icon;
          return (
            <Card key={idx} data-testid={`howto-section-${idx}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className={`h-4 w-4 ${section.color}`} />
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.items.map((it, i) => (
                  <div key={i} className="border-l-2 border-muted pl-3">
                    <div className="text-sm font-medium">{it.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{it.body}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-rose-200/40 bg-rose-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            Before You Go Live (Pre-launch Checklist)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-1.5 list-disc list-inside">
            <li>Switch Stripe to <strong>LIVE mode</strong> (Test → Live toggle in Stripe dashboard).</li>
            <li>Update <code>STRIPE_SECRET_KEY</code>, <code>STRIPE_PUBLISHABLE_KEY</code>, <code>STRIPE_WEBHOOK_SECRET</code> on Render → LIVE values.</li>
            <li>Verify all <strong>Licenses</strong> active and <strong>IP</strong> filings begun.</li>
            <li>Run final <strong>test-account purge</strong> on production database.</li>
            <li>Verify <code>EMERGENT_LLM_KEY</code> set in Render env vars (powers AI + object storage).</li>
            <li>Confirm <strong>legal docs</strong> at /legal/terms and /legal/privacy are reviewed by a lawyer (or note assumptions).</li>
            <li>Set up Discord/email <strong>alerts</strong> for first-paying-customer + first-failed-payment events.</li>
            <li>Document <strong>incident response</strong>: who's on-call, escalation channel.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ExternalLink className="h-4 w-4" /> Key Links (cross-reference Internal Accounts tab)
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1.5">
          <div>• <strong>Stripe Dashboard:</strong> dashboard.stripe.com (toggle Test/Live)</div>
          <div>• <strong>Render:</strong> dashboard.render.com → your services + databases</div>
          <div>• <strong>Resend:</strong> resend.com/emails — view email delivery status, bounces, opens</div>
          <div>• <strong>USPTO (US Trademark):</strong> uspto.gov/trademarks</div>
          <div>• <strong>CIPO (Canadian Trademark):</strong> ic.gc.ca/eic/site/cipointernet-internetopic.nsf</div>
          <div>• <strong>Stripe Tax Registrations:</strong> dashboard.stripe.com/tax/registrations</div>
        </CardContent>
      </Card>
    </div>
  );
}
